import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import toast from 'react-hot-toast';
import { terminateDeployment } from '../service/deploymentService';
import { FiClock, FiAlertCircle } from 'react-icons/fi';

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
  padding: ${theme.spacing[4]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.secondary};
`;

const GuardInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]};
  background: ${theme.colors.light.background.secondary};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: ${theme.spacing[2]};
`;

const GuardImage = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
`;

const WarningBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]};
  background-color: ${theme.colors.accent.warning}20;
  border: 1px solid ${theme.colors.accent.warning};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.light.text.primary};
  font-size: ${theme.typography.fontSize.sm};
`;

const InputIconWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  
  svg {
    position: absolute;
    left: ${theme.spacing[3]};
    color: ${theme.colors.light.text.muted};
    z-index: 1;
  }

  input {
    padding-left: ${theme.spacing[10]};
  }
`;

interface GuardTerminationModalProps {
    guard: {
        id: string;
        name: string;
        guardId: string;
        image?: string;
    } | null;
    clientPath: string;
    isOpen: boolean;
    onClose: () => void;
    onTerminated: (endDate: Date) => void;
    companyId: string;
}

const GuardTerminationModal: React.FC<GuardTerminationModalProps> = ({
    guard,
    clientPath,
    isOpen,
    onClose,
    onTerminated,
    companyId,
}) => {
    const [endDate, setEndDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [saving, setSaving] = useState(false);

    const handleTerminate = async () => {
        if (!guard || !endDate) return;

        setSaving(true);
        try {
            const terminationDate = new Date(endDate);
            await terminateDeployment(guard.id, clientPath, terminationDate, companyId);
            toast.success('Deployment terminated successfully');
            onTerminated(terminationDate);
            onClose();
        } catch (error: any) {
            console.error('Error terminating deployment:', error);
            toast.error('Failed to terminate: ' + (error.message || 'Unknown error'));
        } finally {
            setSaving(false);
        }
    };

    if (!guard) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="End Guard Deployment"
            footer={
                <>
                    <Button variant="secondary" onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleTerminate} disabled={saving || !endDate}>
                        {saving ? 'Terminating...' : 'End Deployment'}
                    </Button>
                </>
            }
        >
            <Form>
                <WarningBox>
                    <FiAlertCircle size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
                    <div>
                        This will end the deployment for this guard. After saving, you will have the option to select a new guard starting the next day.
                    </div>
                </WarningBox>

                <GuardInfo>
                    {guard.image ? (
                        <GuardImage src={guard.image} alt={guard.name} />
                    ) : (
                        <div
                            style={{
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                backgroundColor: theme.colors.light.background.tertiary,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: theme.typography.fontSize.lg,
                                color: theme.colors.light.text.muted,
                            }}
                        >
                            {guard.name?.charAt(0) || '?'}
                        </div>
                    )}
                    <div>
                        <div style={{ fontWeight: theme.typography.fontWeight.bold }}>{guard.name}</div>
                        <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.light.text.secondary }}>
                            {guard.guardId}
                        </div>
                    </div>
                </GuardInfo>

                <FormGroup>
                    <Label>End Date</Label>
                    <InputIconWrapper>
                        <FiClock />
                        <Input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            dark
                            fullWidth
                        />
                    </InputIconWrapper>
                    <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.light.text.secondary, fontStyle: 'italic' }}>
                        Note: The guard will remain active until this date.
                    </div>
                </FormGroup>
            </Form>
        </Modal>
    );
};

export default GuardTerminationModal;
